<!-- SectionIntro360.vue -->
<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue'

import Button from '@/components/Button.vue'

import iconServiceAssessment from '@/components/icons/service/assessment.vue'
import iconServiceSolutions from '@/components/icons/service/solutions.vue'
import iconServiceImplementation from '@/components/icons/service/implementation.vue'
import iconServiceSupport from '@/components/icons/service/support.vue'
import iconServiceImprovment from '~/components/icons/service/improvement.vue'
import SectionIntro360Sphere from './SectionIntro360Sphere.vue'

const router = useRouter()

const { ww } = useViewport()

interface Service {
  index: number
  id: string
  icon: string
  label: string
  shortLabel: string
  description: string
  color: string
}

const services: Service[] = [
  {
    index: 1,
    id: 'assessment',
    icon: 'i-mdi:magnify',
    label: 'Assessment & Discovery',
    shortLabel: 'Tailored assessments to understand your needs.',
    description:
      'We begin by listening. Every site is different, so we carry out detailed assessments to understand your environment, your compliance challenges and your goals. This means you get solutions built around your reality — not off-the-shelf guesses.',
    color: '#22d3ee' // cyan-400
  },
  {
    index: 2,
    id: 'solutions',
    icon: 'i-mdi:lightbulb-outline',
    label: 'Tailored Solutions',
    shortLabel: 'Hygiene plans designed for quality and efficiency.',
    description:
      'We design hygiene systems that combine the right products, dosing equipment and training. Because we source from world-class suppliers, our recommendations give you the perfect balance of quality, eco-friendliness and cost-effectiveness — proven in hotels and facilities across Zanzibar.',
    color: '#a78bfa' // violet-400
  },
  {
    index: 3,
    id: 'implementation',
    icon: 'i-mdi:school-outline',
    label: 'Implementation & Training',
    shortLabel: 'Seamless delivery, expert training.',
    description:
      "We don't just drop off chemicals — we deliver complete solutions. Our technicians install, calibrate and demonstrate equipment. Our trainers empower your staff with the knowledge and skills to clean safely, efficiently and consistently. The result is better standards from day one.",
    color: '#60a5fa' // blue-400
  },
  {
    index: 4,
    id: 'support',
    icon: 'i-mdi:lifebuoy',
    label: 'Ongoing Support',
    shortLabel: 'Technical visits, compliance and monitoring.',
    description:
      'Support is where we shine. Every two months, our technicians check your systems and dispensers. We provide data sheets, compliance signage and hygiene audits. From emergency maintenance to staff refreshers, we keep your operations running smoothly with no downtime.',
    color: '#34d399' // emerald-400
  },
  {
    index: 5,
    id: 'improvement',
    icon: 'i-mdi:trending-up',
    label: 'Continuous Improvement',
    shortLabel: 'Refining and raising standards, year after year.',
    description:
      "Hygiene never stands still — neither do we. We review results, introduce new eco-friendly innovations, and help you refine processes to save time, reduce waste and elevate guest experience. Continuous improvement ensures you're always one step ahead of the competition.",
    color: '#fbbf24' // amber-400
  }
]

// Animation timing (ms)
const DURATION_NODE_MS = 5000  // time for node progress (user ask: 3s)
const DURATION_LINK_MS = 200   // time to colour the link after node completes

// State
const progressKey = ref(0)             // restart CSS animations
const activeIndex = ref(0)             // 0..4
const completedCount = ref(0)          // number of nodes fully complete (locked)
const completedSegments = ref(0)       // number of backbone segments completed
const phase = ref<'node' | 'link' | 'summary'>('node') // node = filling node ring; link = colouring segment
const linkFromIndex = ref(0)           // when in 'link' phase, which segment (i -> i+1) animates
const isPaused = ref(false)

// Timers
let tNode: ReturnType<typeof setTimeout> | null = null
let tLink: ReturnType<typeof setTimeout> | null = null

// Flow control
function clearTimers() {
  if (tNode) { clearTimeout(tNode); tNode = null }
  if (tLink) { clearTimeout(tLink); tLink = null }
}

function runNodePhase() {
  phase.value = 'node'
  progressKey.value++ // restart node ring animation
  clearTimers()
  if (isPaused.value) return
  tNode = setTimeout(() => {
    // Node just finished its 3s ring; lock it as complete
    completedCount.value = Math.max(completedCount.value, activeIndex.value + 1)
    // If that was the last node, go to summary instead of link
    if (completedCount.value >= services.length) {
      completedSegments.value = services.length
      runSummaryPhase()
      return
    }
    // Start link (segment) animation between current node and next
    linkFromIndex.value = activeIndex.value
    runLinkPhase()
  }, DURATION_NODE_MS)
}

function runLinkPhase() {
  phase.value = 'link'
  progressKey.value++ // restart link segment animation
  clearTimers()
  if (isPaused.value) return
  tLink = setTimeout(() => {
    const from = linkFromIndex.value
    // advance to next node after the 200 ms link draw
    completedSegments.value = Math.min(
      services.length,
      Math.max(completedSegments.value, from + 1)
    )
    activeIndex.value = (activeIndex.value + 1) % services.length
    runNodePhase()
  }, DURATION_LINK_MS)
}

function runSummaryPhase() {
  phase.value = 'summary'
  clearTimers()
  if (isPaused.value) return
  // brief summary then reset the loop
  tLink = setTimeout(() => {
    completedCount.value = 0
    completedSegments.value = 0
    activeIndex.value = 0
    runNodePhase()
  }, 2500)
}

// Public selection (click)
function selectServiceById(id: string) {
  const idx = services.findIndex(s => s.id === id)
  if (idx < 0) return
  clearTimers()
  completedCount.value = Math.max(completedCount.value, idx)
  completedSegments.value = Math.min(
    services.length,
    Math.max(completedSegments.value, Math.max(idx, 0))
  )
  // Option: do not roll back completed nodes
  activeIndex.value = idx
  // If user jumps beyond completed, keep completedCount as-is
  phase.value = 'node'
  if (isPaused.value) return
  runNodePhase()
}

function resumeCurrentPhase() {
  if (phase.value === 'link') {
    runLinkPhase()
    return
  }
  if (phase.value === 'summary') {
    runSummaryPhase()
    return
  }
  runNodePhase()
}

function resumePlayback() {
  if (!isPaused.value) return
  isPaused.value = false
  resumeCurrentPhase()
}

function pausePlayback() {
  if (isPaused.value) return
  isPaused.value = true
  clearTimers()
}

function goToNextService() {
  const nextIndex = (activeIndex.value + 1) % services.length
  selectServiceById(services[nextIndex].id)
}

onMounted(runNodePhase)
onUnmounted(clearTimers)

// Bindings
const activeService = computed(() => services[activeIndex.value])
</script>

<template>
  <section class="section-intro-360 gpy-2 d:gpt-4 relative">

    <!-- -translate-x-1/2 left-1/2 -->
    <div v-if="ww > 1024"
      class="row-service-list absolute top-16px right-0 w-100% d:w-90% dm:w-90% transform -translate-y-1/2 z-30 hidden ds:flex">
      <div class="sec-items flex g-1 max-w-92% mx-auto">
        <div v-for="(service, index) in services" :key="service.id"
          class="service-list-item cursor-pointer gp-1 rounded-lg transition-all shadow-md flex bg-white" :class="{
            'active': index === activeIndex
          }" @click="selectServiceById(service.id)">
          <div class="sec-content flex items-start g-0.5 d:g-1 flex-row" :class="service.id">
            <iconServiceAssessment v-if="service.id === 'assessment'" class="assessment fs-15 w-40px! h-40px!"
              :style="{ color: service.color }" />
            <iconServiceSolutions v-if="service.id === 'solutions'" class="solutions fs-15 w-40px! h-40px!"
              :style="{ color: service.color }" />
            <iconServiceImplementation v-if="service.id === 'implementation'"
              class="implementation fs-15 w-40px! h-40px!" :style="{ color: service.color }" />
            <iconServiceSupport v-if="service.id === 'support'" class="support fs-15 w-40px! h-40px!"
              :style="{ color: service.color }" />
            <iconServiceImprovment v-if="service.id === 'improvement'" class="improvement fs-15 w-40px! h-40px!"
              :style="{ color: service.color }" />
            <div>
              <div class="box fs-05 fw-600 flex flex-col">
                <span class="index text-gray-300">0{{ index + 1 }}.</span>
                <span class="title text-gray-900">{{ service.label }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Top Content Row -->
    <div class="intro-top-content grid grid-cols-[1fr] dm:grid-cols-[1.1fr_1fr_1.1fr] g-1 tm:gpy-2 ds:gpt-4 dm:gpt-0 max-w-92% mx-auto">


      <div class="intro-content flex flex-col g-1 relative mx-auto ds:gpy-2">
        <!-- <div class="title-top fs-0.5 uppercase text-gray-400 ls-0.03em">
          Welcome to Altracare
        </div>  -->
        <div class="title fs-4 d:fs-9 fw-300 text-center ds:text-left glh-2">
          We are more than just a supplier. We are your trusted partners in hygiene excellence.
        </div>

        <p class="fs-0">
          Altracare delivers world-class, eco-friendly cleaning solutions designed for industries where first
          impressions define success.
        </p>
        <p class="fs-0">
          With global suppliers, proven systems and local expertise, we help you achieve spotless results that protect
          both reputation and bottom line.
        </p>
        <p class="fs-0">While our products are exceptional, it’s our 360° Service that truly sets us apart.</p>
      </div>
      <SectionIntro360Sphere class="service-circle-container w-100%" :services="services" :active-index="activeIndex"
        :completed-count="completedCount" :completed-segments="completedSegments" :phase="phase"
        :progress-key="progressKey" :link-from-index="linkFromIndex" @select-service="selectServiceById" />

      <div class="service-details flex flex-col items-start w-full justify-center g-1">
        <div class="service-detail-controls flex items-center g-0.5">
          <button
            class="control-button"
            type="button"
            :class="{ active: !isPaused }"
            @click="resumePlayback"
            aria-label="Play service rotation"
          >
            <i class="i-mdi:play fs-15"></i>
          </button>
          <button
            class="control-button"
            type="button"
            :class="{ active: isPaused }"
            @click="pausePlayback"
            aria-label="Pause service rotation"
          >
            <i class="i-mdi:pause fs-15"></i>
          </button>
        </div>
        <Transition name="fade" mode="out-in">
          <div v-if="phase !== 'summary'" :key="activeService.id" class="service-detail-content grid g-1" :class="activeService.id">
            <div class="heading flex flex-col justify-start g-1">
              <div class="small fs-05 text-[var(--base-650)]">0{{ activeService.index }}.</div>
              <h3 class="title fs-3 fw-400 font-secondary! text-[var(--base-700)]">{{ activeService.label }}</h3>
            </div>
            <p class="fs-0 text-[var(--base-600)] leading-relaxed gmb-0.5 glh-1.25">
              {{ activeService.description }}
            </p>

            <div class="sec-action flex items-center g-0.75 g-1!">
              <Button @click="router.push(`/our-service`)" class="gpy-0.75 gpx-1.25 fs--05! glh-1.25!" btn="light-blue">Learn more</Button>
              <button class="next-link text-gray-400" type="button" @click="goToNextService">Next</button>
            </div>
          </div>

          <div v-else key="summary" class="service-detail-content grid g-1 w-100%">
            <div class="flex items-center g-1 gmb-2">
              <div class="small fs-05 text-gray-400">360°</div>
              <h3 class="fs-2 fw-400 font-secondary!">Everything working together</h3>
            </div>
            <p class="fs-0 text-[var(--base-600)] leading-relaxed glh-1.25">
              Our end-to-end programme — assessment, solutions, implementation, support and continuous improvement —
              deliversconsistent standards and lasting confidence.
            </p>
            <div class="sec-action">
              <Button @click="router.push(`/our-service`)" class="gpy-0.75 gpx-1.25" btn="light-blue">Learn more</Button>
            </div>
            
          </div>
        </Transition>
      </div>

    </div>


  </section>
</template>

<style lang="scss" scoped>
.section-intro-360 {
  position: relative;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.service-list-item {
  position: relative;
  overflow: hidden;

  .sec-content {
    :deep(svg) {
      position: relative;
      left: 0px;
      width: 40px !important;
      height: auto !important;
      opacity: 0.5;
      transition: all 0.3s ease;

      @include mq($from: d) {
        width: 60px !important;
      }
    }

    .index {}

    .title {
      color: var(--color-primary);
      font-weight: 400;
      line-height: gr(1.2);
    }




  }

  &.active {
    .sec-content {
      :deep(svg) {
        opacity: 1;
        width: 60px !important;

        @include mq($from: d) {
          width: 80px !important;
        }
      }

      .index {}

      .title {
        color: var(--color-primary);
      }

      &.assessment {
        .box {
          .index {
            color: #65D0EB;
          }
        }
      }

      &.solutions {
        .box {
          .index {
            color: #A78BFA;
          }
        }
      }

      &.implementation {
        .box {
          .index {
            color: #60A5FA;
          }
        }
      }

      &.support {
        .box {
          .index {
            color: #34D399;
          }
        }
      }

      &.improvement {
        .box {
          .index {
            color: #FBBF24;
          }
        }
      }
    }


  }
}


.intro-top-content {
  @include mq($from: t, $until: dm) {
    display: grid;
    grid-template-areas:
      "content content"
      "circle details";
    grid-template-columns: 1fr 1fr;
    grid-template-rows: auto auto;
  }

  >.intro-content {
    @include mq($from: t, $until: dm) {
      grid-area: content;
      width: 100%;
    }
  }

  >.service-circle-container {
    @include mq($from: tm, $until: ds) {
      grid-area: circle;
      //width: 50%;
    }
  }

  >.service-details {
    @include mq($from: tm, $until: ds) {
      grid-area: details;
     // width: 50%;
    }

    .service-detail-controls {
      margin-bottom: 1rem;
    }

    .control-button {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      border: none;
      background: none;
      color: #d1d5db;
      cursor: pointer;
      transition: color 0.2s ease;

      i {
        @apply text-gray-300;
      }

      &.active {
        i {
          color: var(--i-blue);
        }
      }
    }

    .control-button:hover {
      color: #4b5563;
    }

    .control-button.active {
      color: var(--color-primary);
    }

    .control-button + .control-button {
      margin-left: 0.5rem;
    }

    .sec-action {
      display: flex;
      align-items: center;
      gap: 0.75rem;
    }

    .next-link {
      background: none;
      border: none;
      padding: 0;
      font-size: 0.75rem;
      color: var(--i-blue);
      text-transform: uppercase;
      letter-spacing: 0.08em;
      cursor: pointer;
      font-weight: 600;
    }

    .next-link:hover {
      text-decoration: underline;
    }

    .service-detail-content {
      >.heading {
        .small {
          color: #65D0EB;
        }
      }

      &.assessment {
        .heading {
          .small {
            color: #65D0EB;
          }
        }
      }

      &.solutions {
        .heading {
          .small {
            color: #A78BFA;
          }
        }
      }

      &.implementation {
        .heading {
          .small {
            color: #60A5FA;
          }
        }
      }

      &.support {
        .heading {
          .small {
            color: #34D399;
          }
        }
      }

      &.improvement {
        .heading {
          .small {
            color: #FBBF24;
          }
        }
      }
    }
  }
}
</style>
